import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Tag } from 'primereact/tag';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import { Skeleton } from 'primereact/skeleton';
import { Avatar } from 'primereact/avatar';
import { TabMenu } from 'primereact/tabmenu';
import { Menu } from 'primereact/menu';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { Paginator } from 'primereact/paginator';
import { SelectButton } from 'primereact/selectbutton';
import { InputSwitch } from 'primereact/inputswitch';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import lendingNetworkService from '../../services/admin/lendingNetworkService';

/**
 * Per-row kebab menu for employee actions.
 * One Menu instance per row, model pre-bound to rowData via closure.
 */
const RowActionsMenu = ({ rowData, actioning, onApprove, onDeny, onRemove }) => {
    const menuRef = useRef(null);

    const items = useMemo(() => {
        const list = [];
        if (rowData.lending_network_status !== 'approved') {
            list.push({ label: 'Approve', icon: 'pi pi-check', command: () => onApprove(rowData) });
        }
        if (rowData.lending_network_status !== 'denied') {
            list.push({ label: 'Deny', icon: 'pi pi-times', command: () => onDeny(rowData) });
        }
        if (list.length > 0) list.push({ separator: true });
        list.push({ label: 'Remove from network', icon: 'pi pi-trash', command: () => onRemove(rowData) });
        return list;
    }, [rowData, onApprove, onDeny, onRemove]);

    return (
        <>
            <Menu
                model={items}
                popup
                ref={menuRef}
                appendTo={typeof document !== 'undefined' ? document.body : undefined}
            />
            <Button
                icon="pi pi-ellipsis-v"
                rounded
                text
                size="small"
                aria-label="Employee actions"
                disabled={actioning}
                onClick={(e) => menuRef.current?.toggle(e)}
            />
        </>
    );
};

const STATUS_SEVERITY = { pending: 'warning', approved: 'success', denied: 'danger' };
const STATUS_LABEL = { pending: 'Pending', approved: 'Approved', denied: 'Denied' };

const POST_STATUS_SEVERITY = { pending: 'warning', approved: 'success', rejected: 'danger' };
const POST_STATUS_LABEL = { pending: 'Awaiting review', approved: 'Published', rejected: 'Rejected' };

/**
 * Mobile AccordionTab header — name + status tag (kept minimal so chevron
 * renders consistently across all rows).
 */
const MemberHeader = ({ member }) => {
    const status = member.lending_network_status;
    return (
        <div className="flex justify-content-between align-items-center gap-2 w-full">
            <span className="flex-1 min-w-0 font-medium text-sm text-900 white-space-nowrap overflow-hidden text-overflow-ellipsis">
                {member.full_name || '—'}
            </span>
            <Tag
                value={STATUS_LABEL[status] || status || '—'}
                severity={STATUS_SEVERITY[status] || 'info'}
                className="text-xs flex-shrink-0"
            />
        </div>
    );
};

/**
 * Expanded content for mobile accordion — mirrors desktop columns + action buttons.
 */
const MemberDetails = ({ member, actioning, onApprove, onDeny, onRemove }) => {
    const status = member.lending_network_status;
    const actionAt = member.lending_network_approved_at;
    const dateText = actionAt && status !== 'pending'
        ? `${new Date(actionAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })} · ${new Date(actionAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })}`
        : '—';
    const dateIcon = status === 'approved' ? 'pi-check-circle text-green-600'
        : status === 'denied' ? 'pi-times-circle text-red-500'
        : 'pi-clock text-500';

    return (
        <div className="flex flex-column gap-2 text-xs">
            <div className="flex align-items-center gap-2">
                <i className="pi pi-envelope text-500 w-1rem" aria-hidden="true" />
                <span className="text-500 font-medium w-6rem">Email</span>
                <span className={member.email ? 'text-700' : 'text-500'}>{member.email || '—'}</span>
            </div>
            <div className="flex align-items-center gap-2">
                <i className="pi pi-phone text-500 w-1rem" aria-hidden="true" />
                <span className="text-500 font-medium w-6rem">Phone</span>
                <span className={member.phone ? 'text-700' : 'text-500'}>{member.phone || '—'}</span>
            </div>
            <div className="flex align-items-center gap-2">
                <i className="pi pi-briefcase text-500 w-1rem" aria-hidden="true" />
                <span className="text-500 font-medium w-6rem">Role / Headline</span>
                <span className={member.headline ? 'text-700' : 'text-500'}>{member.headline || '—'}</span>
            </div>
            <div className="flex align-items-center gap-2">
                <i className={`pi ${dateIcon} w-1rem`} aria-hidden="true" />
                <span className="text-500 font-medium w-6rem">Action Date</span>
                <span className={actionAt && status !== 'pending' ? 'text-700' : 'text-500'}>{dateText}</span>
            </div>
            <div className="flex align-items-start gap-2">
                <i className="pi pi-info-circle text-500 w-1rem mt-1" aria-hidden="true" />
                <span className="text-500 font-medium w-6rem mt-1">Denial Reason</span>
                <span className={`line-height-3 ${member.lending_network_denied_reason ? 'text-600' : 'text-500'}`}>
                    {member.lending_network_denied_reason || '—'}
                </span>
            </div>

            <div className="flex flex-wrap gap-2 mt-3 pt-3 border-top-1 surface-border">
                {status !== 'approved' && (
                    <Button
                        label="Approve"
                        icon="pi pi-check"
                        severity="success"
                        outlined
                        size="small"
                        disabled={actioning}
                        onClick={() => onApprove(member)}
                    />
                )}
                {status !== 'denied' && (
                    <Button
                        label="Deny"
                        icon="pi pi-times"
                        severity="danger"
                        outlined
                        size="small"
                        disabled={actioning}
                        onClick={() => onDeny(member)}
                    />
                )}
                <Button
                    label="Remove"
                    icon="pi pi-trash"
                    severity="secondary"
                    outlined
                    size="small"
                    disabled={actioning}
                    onClick={() => onRemove(member)}
                />
            </div>
        </div>
    );
};

/**
 * Company Admin — Lending Network Management (v2.0)
 *
 * Route: /app/company/lending-network
 *
 * Responsibilities:
 *   1. View / filter company employees by lending-network status
 *   2. Approve, Deny (with reason), Remove employees
 */
const CompanyLendingNetworkAdmin = () => {
    const toast = useRef(null);

    // Settings
    const [approvalMode, setApprovalMode] = useState('auto');
    const [loadingSettings, setLoadingSettings] = useState(true);
    const [savingSettings, setSavingSettings] = useState(false);

    // Visibility policy for this company's employees and agents.
    // Auto-saved on change (P19) — each switch is independent and safe alone.
    const [policy, setPolicy] = useState({
        profiles_enabled: true,
        profile_visibility: 'public',
        post_visibility: 'public',
        post_approval_required: false,
    });
    const [tenantCeiling, setTenantCeiling] = useState({ profile_visibility: 'public', post_visibility: 'public' });
    const [effective, setEffective] = useState({ profile_visibility: 'public', post_visibility: 'public' });
    const [policyOptions, setPolicyOptions] = useState([]);
    const [loadingPolicy, setLoadingPolicy] = useState(true);
    const [savingPolicy, setSavingPolicy] = useState(false);

    // Post approval queue
    const [posts, setPosts] = useState([]);
    const [postCounts, setPostCounts] = useState({ pending: 0, approved: 0, rejected: 0 });
    const [postStatusIndex, setPostStatusIndex] = useState(0);
    const [postsLoading, setPostsLoading] = useState(false);
    const [postsTotal, setPostsTotal] = useState(0);
    const [postsPage, setPostsPage] = useState(1);
    const [postActioning, setPostActioning] = useState(false);
    const [rejectDialogVisible, setRejectDialogVisible] = useState(false);
    const [selectedPost, setSelectedPost] = useState(null);
    const [rejectReason, setRejectReason] = useState('');

    // Members
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [totalRecords, setTotalRecords] = useState(0);
    const [counts, setCounts] = useState({ pending: 0, approved: 0, denied: 0 });
    const [activeTabIndex, setActiveTabIndex] = useState(0);
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [page, setPage] = useState(1);
    const limit = 10;

    // Deny dialog
    const [denyDialogVisible, setDenyDialogVisible] = useState(false);
    const [selectedProfile, setSelectedProfile] = useState(null);
    const [denyReason, setDenyReason] = useState('');
    const [actioning, setActioning] = useState(false);

    // Mobile accordion state — one open at a time
    const [mobileActiveIndex, setMobileActiveIndex] = useState(null);
    useEffect(() => {
        setMobileActiveIndex(null);
    }, [activeTabIndex, debouncedSearch, page]);

    const tabStatuses = useMemo(() => ['all', 'pending', 'approved', 'denied'], []);

    const approvalModeOptions = useMemo(() => [
        { label: 'Auto Approve', value: 'auto' },
        { label: 'Manual Approval', value: 'manual' },
    ], []);

    const totalMembers = counts.pending + counts.approved + counts.denied;

    const tabMenuModel = useMemo(() => [
        { label: 'All' },
        { label: `Pending (${counts.pending})` },
        { label: `Approved (${counts.approved})` },
        { label: `Denied (${counts.denied})` },
    ], [counts]);

    // Debounced search (P13)
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(search.trim());
            setPage(1);
        }, 500);
        return () => clearTimeout(timer);
    }, [search]);

    // Load settings once
    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const res = await lendingNetworkService.getCompanySettings();
                if (res.success) setApprovalMode(res.data.approval_mode);
            } catch (e) {
                console.error('Failed to load settings:', e?.message || 'Unknown error');
            } finally {
                setLoadingSettings(false);
            }
        };
        fetchSettings();
    }, []);

    const handleSaveSettings = async () => {
        setSavingSettings(true);
        try {
            const res = await lendingNetworkService.updateCompanySettings(approvalMode);
            if (res.success) {
                toast.current?.show({ severity: 'success', summary: 'Saved', detail: 'Approval mode updated' });
            } else {
                throw new Error(res.message);
            }
        } catch (e) {
            toast.current?.show({ severity: 'error', summary: 'Error', detail: e?.message || 'Failed to save settings' });
        } finally {
            setSavingSettings(false);
        }
    };

    // ── Visibility policy ─────────────────────────────────────────────────

    const applyVisibilityResponse = useCallback((data) => {
        setPolicy({
            profiles_enabled: !!data.profiles_enabled,
            profile_visibility: data.profile_visibility,
            post_visibility: data.post_visibility,
            post_approval_required: !!data.post_approval_required,
        });
        setTenantCeiling(data.tenant_ceiling || { profile_visibility: 'public', post_visibility: 'public' });
        setEffective(data.effective || { profile_visibility: 'public', post_visibility: 'public' });
        setPolicyOptions(data.options || []);
    }, []);

    useEffect(() => {
        const fetchPolicy = async () => {
            try {
                const res = await lendingNetworkService.getCompanyVisibility();
                if (res.success) applyVisibilityResponse(res.data);
            } catch (e) {
                console.error('Failed to load visibility policy:', e?.message || 'Unknown error');
            } finally {
                setLoadingPolicy(false);
            }
        };
        fetchPolicy();
    }, [applyVisibilityResponse]);

    /**
     * Auto-save a policy change (P19): optimistic update, rollback on failure.
     * The whole policy is sent every time because the endpoint replaces it —
     * sending one field would blank the rest.
     */
    const persistPolicy = useCallback(async (patch, successDetail) => {
        const previous = policy;
        const next = { ...policy, ...patch };
        setPolicy(next);
        setSavingPolicy(true);
        try {
            const res = await lendingNetworkService.updateCompanyVisibility(next);
            if (!res.success) throw new Error(res.message);

            // Re-read so `effective` reflects any tenant-ceiling clamp the
            // server applied — otherwise a capped setting looks like it saved
            // one thing and did another.
            const fresh = await lendingNetworkService.getCompanyVisibility();
            if (fresh.success) applyVisibilityResponse(fresh.data);

            toast.current?.show({ severity: 'success', summary: 'Saved', detail: successDetail, life: 2000 });
        } catch (e) {
            setPolicy(previous);
            toast.current?.show({
                severity: 'error',
                summary: 'Save failed',
                detail: e?.message || 'Could not save your change',
                life: 3500,
            });
        } finally {
            setSavingPolicy(false);
        }
    }, [policy, applyVisibilityResponse]);

    // ── Post approval queue ───────────────────────────────────────────────

    const postStatuses = useMemo(() => ['pending', 'approved', 'rejected'], []);
    const postsLimit = 10;

    const postTabModel = useMemo(() => [
        { label: `Pending (${postCounts.pending})` },
        { label: `Approved (${postCounts.approved})` },
        { label: `Rejected (${postCounts.rejected})` },
    ], [postCounts]);

    const loadPosts = useCallback(async () => {
        setPostsLoading(true);
        try {
            const res = await lendingNetworkService.getCompanyPosts({
                status: postStatuses[postStatusIndex],
                page: postsPage,
                limit: postsLimit,
            });
            if (res.success) {
                setPosts(res.data.posts || []);
                setPostsTotal(res.data.pagination?.total || 0);
                setPostCounts(res.data.counts || { pending: 0, approved: 0, rejected: 0 });
            }
        } catch (e) {
            console.error('Failed to load posts:', e?.message || 'Unknown error');
        } finally {
            setPostsLoading(false);
        }
    }, [postStatusIndex, postsPage, postStatuses]);

    // Only fetch the queue when approval is actually switched on — otherwise
    // the section isn't rendered and the request would be wasted.
    useEffect(() => {
        if (!policy.post_approval_required) return;
        loadPosts();
    }, [policy.post_approval_required, loadPosts]);

    const handleApprovePost = useCallback(async (post) => {
        setPostActioning(true);
        try {
            const res = await lendingNetworkService.approveCompanyPost(post.id);
            if (!res.success) throw new Error(res.message);
            toast.current?.show({ severity: 'success', summary: 'Approved', detail: 'Post published to the network' });
            loadPosts();
        } catch (e) {
            toast.current?.show({ severity: 'error', summary: 'Error', detail: e?.message || 'Failed to approve post' });
        } finally {
            setPostActioning(false);
        }
    }, [loadPosts]);

    const openRejectDialog = useCallback((post) => {
        setSelectedPost(post);
        setRejectReason('');
        setRejectDialogVisible(true);
    }, []);

    const handleRejectPost = async () => {
        if (!selectedPost) return;
        setPostActioning(true);
        try {
            const res = await lendingNetworkService.rejectCompanyPost(selectedPost.id, rejectReason.trim());
            if (!res.success) throw new Error(res.message);
            toast.current?.show({ severity: 'info', summary: 'Rejected', detail: 'Post will not be published' });
            setRejectDialogVisible(false);
            loadPosts();
        } catch (e) {
            toast.current?.show({ severity: 'error', summary: 'Error', detail: e?.message || 'Failed to reject post' });
        } finally {
            setPostActioning(false);
        }
    };

    // Load members (P14 — primitive deps only)
    useEffect(() => {
        let cancelled = false;
        const fetchMembers = async () => {
            setLoading(true);
            try {
                const status = tabStatuses[activeTabIndex];
                const res = await lendingNetworkService.getCompanyMembers({
                    status,
                    search: debouncedSearch || undefined,
                    page,
                    limit,
                });
                if (cancelled) return;
                if (res.success) {
                    setMembers(res.data.members || []);
                    setTotalRecords(res.data.pagination?.total || 0);
                    setCounts(res.data.counts || { pending: 0, approved: 0, denied: 0 });
                }
            } catch (e) {
                if (!cancelled) {
                    console.error('Failed to load members:', e?.message || 'Unknown error');
                    toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to load members' });
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        fetchMembers();
        return () => { cancelled = true; };
    }, [activeTabIndex, debouncedSearch, page, tabStatuses]);

    const reloadMembers = useCallback(async () => {
        setLoading(true);
        try {
            const status = tabStatuses[activeTabIndex];
            const res = await lendingNetworkService.getCompanyMembers({
                status,
                search: debouncedSearch || undefined,
                page,
                limit,
            });
            if (res.success) {
                setMembers(res.data.members || []);
                setTotalRecords(res.data.pagination?.total || 0);
                setCounts(res.data.counts || { pending: 0, approved: 0, denied: 0 });
            }
        } catch (e) {
            console.error('Failed to reload members:', e?.message || 'Unknown error');
        } finally {
            setLoading(false);
        }
    }, [activeTabIndex, debouncedSearch, page, tabStatuses]);

    const handleApprove = useCallback(async (profile) => {
        setActioning(true);
        try {
            const res = await lendingNetworkService.approveCompanyMember(profile.id);
            if (res.success) {
                toast.current?.show({
                    severity: 'success',
                    summary: 'Approved',
                    detail: `${profile.full_name || profile.email} approved`,
                });
                reloadMembers();
            } else {
                throw new Error(res.message);
            }
        } catch (e) {
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: e?.message || 'Failed to approve member',
            });
        } finally {
            setActioning(false);
        }
    }, [reloadMembers]);

    const openDenyDialog = useCallback((profile) => {
        setSelectedProfile(profile);
        setDenyReason('');
        setDenyDialogVisible(true);
    }, []);

    const handleDeny = async () => {
        if (!selectedProfile) return;
        setActioning(true);
        try {
            const res = await lendingNetworkService.denyCompanyMember(selectedProfile.id, denyReason.trim());
            if (res.success) {
                toast.current?.show({
                    severity: 'info',
                    summary: 'Denied',
                    detail: `${selectedProfile.full_name || selectedProfile.email} denied`,
                });
                setDenyDialogVisible(false);
                reloadMembers();
            } else {
                throw new Error(res.message);
            }
        } catch (e) {
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: e?.message || 'Failed to deny member',
            });
        } finally {
            setActioning(false);
        }
    };

    const handleRemove = useCallback((profile) => {
        confirmDialog({
            message: `Remove ${profile.full_name || profile.email} from the Lending Network?`,
            header: 'Confirm Remove',
            icon: 'pi pi-exclamation-triangle',
            acceptClassName: 'p-button-danger',
            accept: async () => {
                try {
                    const res = await lendingNetworkService.removeCompanyMember(profile.id);
                    if (res.success) {
                        toast.current?.show({
                            severity: 'warn',
                            summary: 'Removed',
                            detail: `${profile.full_name || profile.email} removed`,
                        });
                        reloadMembers();
                    } else {
                        toast.current?.show({
                            severity: 'error',
                            summary: 'Error',
                            detail: res.message || 'Failed to remove member',
                        });
                    }
                } catch (e) {
                    toast.current?.show({
                        severity: 'error',
                        summary: 'Error',
                        detail: e?.message || 'Failed to remove member',
                    });
                }
            },
        });
    }, [reloadMembers]);

    // KPI card (P15). `accent` adds a left border when value > 0
    const KpiCard = ({ icon, label, value, description, accent }) => {
        const accentStyle = accent && value > 0
            ? { borderLeft: `3px solid var(--${accent}-500)` }
            : undefined;
        return (
            <div className="col-6 lg:col-3">
                <Card className="h-full shadow-1" style={accentStyle} pt={{ body: { className: 'p-3' } }}>
                    <div className="flex justify-content-between align-items-start">
                        <div className="flex flex-column gap-1">
                            <span className="text-600 text-sm font-medium">{label}</span>
                            <span className="text-900 text-xl font-semibold">{value}</span>
                        </div>
                        <div className="flex align-items-center justify-content-center border-round bg-primary-50 p-2">
                            <i className={`pi ${icon} text-xl text-primary`} aria-hidden="true"></i>
                        </div>
                    </div>
                    <span className="text-500 text-xs block mt-2">{description}</span>
                </Card>
            </div>
        );
    };

    // DataTable column templates
    const memberTemplate = useCallback((rowData) => (
        <div className="flex align-items-center gap-2">
            <Avatar
                image={rowData.profile_photo_url_full}
                label={rowData.full_name?.charAt(0)?.toUpperCase()}
                shape="circle"
                size="normal"
                className="flex-shrink-0"
            />
            <div className="flex flex-column">
                <span className="font-medium text-sm text-900">{rowData.full_name || '—'}</span>
                <span className="text-xs text-500">{rowData.email || '—'}</span>
            </div>
        </div>
    ), []);

    const phoneTemplate = useCallback((rowData) => (
        <span className="text-sm text-700">{rowData.phone || '—'}</span>
    ), []);

    const headlineTemplate = useCallback((rowData) => (
        <span className="text-sm text-700">{rowData.headline || '—'}</span>
    ), []);

    const statusTemplate = useCallback((rowData) => {
        const status = rowData.lending_network_status;
        return <Tag value={STATUS_LABEL[status] || status || '—'} severity={STATUS_SEVERITY[status] || 'info'} />;
    }, []);

    const actionDateTemplate = useCallback((rowData) => {
        const { lending_network_status: status, lending_network_approved_at: actionAt } = rowData;
        if (status === 'pending' || !actionAt) return <span className="text-500 text-xs">—</span>;
        const d = new Date(actionAt);
        const icon = status === 'approved' ? 'pi-check-circle' : 'pi-times-circle';
        const iconColor = status === 'approved' ? 'text-green-600' : 'text-red-500';
        return (
            <div className="flex flex-column gap-1">
                <div className="flex align-items-center gap-1">
                    <i className={`pi ${icon} ${iconColor} text-xs`} aria-hidden="true" />
                    <span className="text-sm text-900">
                        {d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                    </span>
                </div>
                <span className="text-xs text-500">
                    {d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })}
                </span>
            </div>
        );
    }, []);

    const denialReasonTemplate = useCallback((rowData) => (
        <span className="text-sm text-700">{rowData.lending_network_denied_reason || '—'}</span>
    ), []);

    const actionsTemplate = useCallback((rowData) => (
        <RowActionsMenu
            rowData={rowData}
            actioning={actioning}
            onApprove={handleApprove}
            onDeny={openDenyDialog}
            onRemove={handleRemove}
        />
    ), [actioning, handleApprove, openDenyDialog, handleRemove]);

    return (
        <>
            <Toast ref={toast} />
            <ConfirmDialog />

            {/* Page header (P6) */}
            <div className="flex flex-column md:flex-row md:justify-content-between md:align-items-center gap-3 mb-4">
                <div>
                    <h1 className="text-xl font-semibold text-900 m-0 mb-1">Company Lending Network</h1>
                    <p className="text-600 text-sm m-0">Manage your company employees&apos; lending network access</p>
                </div>
            </div>

            {/* KPI cards (P15) */}
            <div className="grid mb-3">
                <KpiCard
                    icon="pi-users"
                    label="Total Employees"
                    value={totalMembers}
                    description="Employees across all statuses"
                />
                <KpiCard
                    icon="pi-clock"
                    label="Pending"
                    value={counts.pending}
                    description="Awaiting your approval"
                    accent="orange"
                />
                <KpiCard
                    icon="pi-check-circle"
                    label="Approved"
                    value={counts.approved}
                    description="Active in the network"
                />
                <KpiCard
                    icon="pi-ban"
                    label="Denied"
                    value={counts.denied}
                    description="Rejected employees"
                />
            </div>

            {/* Approval Mode — collapsed by default (set-once configuration) */}
            <Accordion className="mb-3">
                <AccordionTab
                    header={
                        <span className="flex align-items-center gap-2 text-sm font-medium">
                            <i className="pi pi-cog text-primary" aria-hidden="true" />
                            <span>Approval Mode</span>
                            <Tag
                                value={approvalMode === 'auto' ? 'Auto Approve' : 'Manual Approval'}
                                severity={approvalMode === 'auto' ? 'success' : 'warning'}
                                className="text-xs ml-1"
                            />
                        </span>
                    }
                >
                    <div className="flex flex-column md:flex-row md:justify-content-between md:align-items-start gap-3">
                        <div className="flex-1">
                            <p className="text-600 text-sm line-height-3 m-0">
                                <strong className="text-900">Auto:</strong> New employee profiles are approved immediately.{' '}
                                <strong className="text-900">Manual:</strong> New employee profiles start as &quot;Pending&quot; — you must approve each one.
                            </p>
                            <p className="text-500 text-xs m-0 mt-2">
                                Changing this only affects <strong>new</strong> employee profile creations. Existing approved profiles remain unchanged.
                            </p>
                        </div>
                        {loadingSettings ? (
                            <Skeleton width="14rem" height="2.5rem" />
                        ) : (
                            <div className="flex flex-wrap align-items-center gap-2">
                                <SelectButton
                                    value={approvalMode}
                                    onChange={(e) => { if (e.value) setApprovalMode(e.value); }}
                                    options={approvalModeOptions}
                                />
                                <Button
                                    label="Save"
                                    icon="pi pi-save"
                                    size="small"
                                    loading={savingSettings}
                                    onClick={handleSaveSettings}
                                />
                            </div>
                        )}
                    </div>
                </AccordionTab>
            </Accordion>

            {/*
              Visibility policy for this company's employees and agents.

              The company admin is the SOLE controller here — members cannot
              override it. Every control shows the effective result alongside
              the chosen value, because the tenant admin's ceiling can clamp a
              choice and a setting that silently does nothing reads as broken.
            */}
            <Accordion className="mb-3">
                <AccordionTab
                    header={
                        <span className="flex align-items-center gap-2 text-sm font-medium">
                            <i className="pi pi-eye text-primary" aria-hidden="true" />
                            <span>Visibility &amp; Post Approval</span>
                            {policy.post_approval_required && postCounts.pending > 0 && (
                                <Tag value={`${postCounts.pending} to review`} severity="warning" className="text-xs ml-1" />
                            )}
                        </span>
                    }
                >
                    {loadingPolicy ? (
                        <div className="flex flex-column gap-3">
                            <Skeleton height="3rem" />
                            <Skeleton height="3rem" />
                            <Skeleton height="3rem" />
                        </div>
                    ) : (
                        <div className="flex flex-column gap-4">
                            <p className="text-600 text-sm line-height-3 m-0">
                                You control how far your employees&apos; and agents&apos; profiles and posts
                                reach. Members cannot change these for themselves.
                            </p>

                            {/* Master switch — profiles visible outside the company at all */}
                            <div className="flex flex-column md:flex-row md:justify-content-between md:align-items-center gap-3">
                                <div className="flex-1">
                                    <div className="flex align-items-center gap-2 mb-1">
                                        <i className="pi pi-users text-500" aria-hidden="true" />
                                        <span className="text-900 text-sm font-medium">Show profiles outside the company</span>
                                    </div>
                                    <p className="text-500 text-xs m-0 line-height-3">
                                        Turn off to keep every member&apos;s profile visible only to your own
                                        company, whatever the level below says.
                                    </p>
                                </div>
                                <InputSwitch
                                    checked={policy.profiles_enabled}
                                    disabled={savingPolicy}
                                    onChange={(e) => persistPolicy(
                                        { profiles_enabled: e.value },
                                        e.value ? 'Profiles visible outside the company' : 'Profiles are now company-only',
                                    )}
                                    aria-label="Show member profiles outside the company"
                                />
                            </div>

                            {/* Profile reach */}
                            <div className="flex flex-column md:flex-row md:justify-content-between md:align-items-center gap-3">
                                <div className="flex-1">
                                    <div className="flex align-items-center gap-2 mb-1">
                                        <i className="pi pi-id-card text-500" aria-hidden="true" />
                                        <span className="text-900 text-sm font-medium">Profile visibility</span>
                                    </div>
                                    <p className="text-500 text-xs m-0 line-height-3">
                                        How far member profiles reach in network search and suggestions.
                                    </p>
                                    {effective.profile_visibility !== policy.profile_visibility && policy.profiles_enabled && (
                                        <p className="text-orange-600 text-xs m-0 mt-1 line-height-3">
                                            <i className="pi pi-exclamation-circle mr-1" aria-hidden="true" />
                                            Applied as <strong>{effective.profile_visibility}</strong> — your tenant admin
                                            limits profiles to <strong>{tenantCeiling.profile_visibility}</strong>.
                                        </p>
                                    )}
                                </div>
                                <SelectButton
                                    value={policy.profile_visibility}
                                    options={policyOptions}
                                    optionLabel="label"
                                    optionValue="value"
                                    disabled={savingPolicy || !policy.profiles_enabled}
                                    onChange={(e) => {
                                        if (!e.value || e.value === policy.profile_visibility) return;
                                        persistPolicy({ profile_visibility: e.value }, 'Profile visibility updated');
                                    }}
                                    aria-label="Member profile visibility"
                                />
                            </div>

                            {/* Post reach */}
                            <div className="flex flex-column md:flex-row md:justify-content-between md:align-items-center gap-3">
                                <div className="flex-1">
                                    <div className="flex align-items-center gap-2 mb-1">
                                        <i className="pi pi-comments text-500" aria-hidden="true" />
                                        <span className="text-900 text-sm font-medium">Post visibility</span>
                                    </div>
                                    <p className="text-500 text-xs m-0 line-height-3">
                                        How far member posts reach in the Lending Network feed.
                                    </p>
                                    {effective.post_visibility !== policy.post_visibility && (
                                        <p className="text-orange-600 text-xs m-0 mt-1 line-height-3">
                                            <i className="pi pi-exclamation-circle mr-1" aria-hidden="true" />
                                            Applied as <strong>{effective.post_visibility}</strong> — your tenant admin
                                            limits posts to <strong>{tenantCeiling.post_visibility}</strong>.
                                        </p>
                                    )}
                                </div>
                                <SelectButton
                                    value={policy.post_visibility}
                                    options={policyOptions}
                                    optionLabel="label"
                                    optionValue="value"
                                    disabled={savingPolicy}
                                    onChange={(e) => {
                                        if (!e.value || e.value === policy.post_visibility) return;
                                        persistPolicy({ post_visibility: e.value }, 'Post visibility updated');
                                    }}
                                    aria-label="Member post visibility"
                                />
                            </div>

                            {/* Post approval */}
                            <div className="flex flex-column md:flex-row md:justify-content-between md:align-items-center gap-3">
                                <div className="flex-1">
                                    <div className="flex align-items-center gap-2 mb-1">
                                        <i className="pi pi-check-square text-500" aria-hidden="true" />
                                        <span className="text-900 text-sm font-medium">Approve posts before publishing</span>
                                    </div>
                                    <p className="text-500 text-xs m-0 line-height-3">
                                        Members&apos; posts wait for your approval instead of publishing straight
                                        away. They can still see their own pending posts.
                                    </p>
                                </div>
                                <InputSwitch
                                    checked={policy.post_approval_required}
                                    disabled={savingPolicy}
                                    onChange={(e) => persistPolicy(
                                        { post_approval_required: e.value },
                                        e.value ? 'New posts now need your approval' : 'Posts publish immediately',
                                    )}
                                    aria-label="Require approval before posts publish"
                                />
                            </div>
                        </div>
                    )}
                </AccordionTab>
            </Accordion>

            {/* Post approval queue — only rendered when approval is switched on */}
            {policy.post_approval_required && (
                <Card className="mb-3">
                    <div className="flex flex-column md:flex-row md:justify-content-between md:align-items-center gap-2 mb-3">
                        <div>
                            <h2 className="text-lg font-semibold text-900 m-0 mb-1">Post Approvals</h2>
                            <p className="text-600 text-sm m-0">
                                Posts written by your employees and agents
                            </p>
                        </div>
                        <Button
                            label="Refresh"
                            icon="pi pi-refresh"
                            severity="secondary"
                            outlined
                            size="small"
                            loading={postsLoading}
                            onClick={loadPosts}
                        />
                    </div>

                    <TabMenu
                        model={postTabModel}
                        activeIndex={postStatusIndex}
                        onTabChange={(e) => { setPostStatusIndex(e.index); setPostsPage(1); }}
                        className="mb-3"
                    />

                    {postsLoading && (
                        <div className="flex flex-column gap-2">
                            <Skeleton height="90px" />
                            <Skeleton height="90px" />
                        </div>
                    )}

                    {!postsLoading && posts.length === 0 && (
                        <div className="flex flex-column align-items-center py-5">
                            <i className="pi pi-inbox pi-2x text-400 mb-3" aria-hidden="true" />
                            <span className="text-600">
                                {postStatuses[postStatusIndex] === 'pending'
                                    ? 'Nothing waiting for review'
                                    : 'No posts here'}
                            </span>
                        </div>
                    )}

                    {!postsLoading && posts.length > 0 && (
                        <>
                            <div className="flex flex-column gap-2">
                                {posts.map((post) => (
                                    <div
                                        key={post.id}
                                        className="border-1 surface-border border-round p-3 surface-0"
                                    >
                                        <div className="flex justify-content-between align-items-start gap-2 mb-2">
                                            <div className="flex align-items-center gap-2 min-w-0">
                                                <Avatar
                                                    image={post.author?.profile_photo_url}
                                                    label={post.author?.full_name?.charAt(0)?.toUpperCase()}
                                                    shape="circle"
                                                    size="normal"
                                                    className="flex-shrink-0"
                                                />
                                                <div className="flex flex-column min-w-0">
                                                    <span className="font-medium text-sm text-900 white-space-nowrap overflow-hidden text-overflow-ellipsis">
                                                        {post.author?.full_name || '—'}
                                                    </span>
                                                    <span className="text-xs text-500">
                                                        {post.created_at
                                                            ? new Date(post.created_at).toLocaleDateString('en-IN', {
                                                                day: '2-digit', month: 'short', year: 'numeric',
                                                            })
                                                            : '—'}
                                                    </span>
                                                </div>
                                            </div>
                                            <Tag
                                                value={POST_STATUS_LABEL[post.approval_status] || post.approval_status}
                                                severity={POST_STATUS_SEVERITY[post.approval_status] || 'info'}
                                                className="text-xs flex-shrink-0"
                                            />
                                        </div>

                                        <p className="text-sm text-700 line-height-3 m-0 mb-2" style={{ whiteSpace: 'pre-wrap' }}>
                                            {post.content || <span className="text-500">(media only)</span>}
                                        </p>

                                        {post.moderation_reason && (
                                            <p className="text-xs text-600 line-height-3 m-0 mb-2">
                                                <i className="pi pi-info-circle mr-1" aria-hidden="true" />
                                                {post.moderation_reason}
                                            </p>
                                        )}

                                        {/* A decision is always reversible — an admin who
                                            approved by mistake can still pull the post back. */}
                                        <div className="flex flex-wrap gap-2 pt-2 border-top-1 surface-border">
                                            {post.approval_status !== 'approved' && (
                                                <Button
                                                    label="Approve"
                                                    icon="pi pi-check"
                                                    severity="success"
                                                    outlined
                                                    size="small"
                                                    disabled={postActioning}
                                                    onClick={() => handleApprovePost(post)}
                                                />
                                            )}
                                            {post.approval_status !== 'rejected' && (
                                                <Button
                                                    label="Reject"
                                                    icon="pi pi-times"
                                                    severity="danger"
                                                    outlined
                                                    size="small"
                                                    disabled={postActioning}
                                                    onClick={() => openRejectDialog(post)}
                                                />
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <Paginator
                                first={(postsPage - 1) * postsLimit}
                                rows={postsLimit}
                                totalRecords={postsTotal}
                                onPageChange={(e) => setPostsPage(Math.floor(e.first / postsLimit) + 1)}
                                template="PrevPageLink PageLinks NextPageLink CurrentPageReport"
                                currentPageReportTemplate="{first}-{last} of {totalRecords}"
                                pageLinkSize={3}
                                className="text-sm mt-2"
                            />
                        </>
                    )}
                </Card>
            )}

            <Card>
                {/* Filter bar (P7 — 1 filter pattern) — INSIDE Card (P20) */}
                <div className="surface-ground p-3 border-round mb-3">
                    <div className="grid">
                        <div className="col-12 md:col">
                            <IconField iconPosition="left" className="w-full">
                                <InputIcon className="pi pi-search" />
                                <InputText
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    placeholder="Search by name or email"
                                    maxLength={100}
                                    className="w-full p-inputtext-sm"
                                />
                            </IconField>
                        </div>
                        <div className="hidden md:block md:col" aria-hidden="true"></div>
                        <div className="hidden md:block md:col" aria-hidden="true"></div>
                        <div className="hidden md:block md:col" aria-hidden="true"></div>
                        <div className="col-12 md:col flex gap-2">
                            <Button
                                label="Clear"
                                icon="pi pi-filter-slash"
                                severity="secondary"
                                outlined
                                size="small"
                                className="flex-1 py-1 md:py-2"
                                onClick={() => setSearch('')}
                                disabled={!search}
                            />
                        </div>
                    </div>
                </div>

                {/* Status tab filter */}
                <TabMenu
                    model={tabMenuModel}
                    activeIndex={activeTabIndex}
                    onTabChange={(e) => { setActiveTabIndex(e.index); setPage(1); }}
                    className="mb-3"
                />

                {/* Mobile (< md): Accordion */}
                <div className="block md:hidden">
                    {loading && (
                        <div className="flex flex-column gap-2">
                            <Skeleton height="70px" />
                            <Skeleton height="70px" />
                            <Skeleton height="70px" />
                        </div>
                    )}
                    {!loading && members.length === 0 && (
                        <div className="flex flex-column align-items-center py-5">
                            <i className="pi pi-users pi-2x text-400 mb-3" aria-hidden="true" />
                            <span className="text-600">No employees found</span>
                        </div>
                    )}
                    {!loading && members.length > 0 && (
                        <>
                            <Accordion
                                activeIndex={mobileActiveIndex}
                                onTabChange={(e) => setMobileActiveIndex(e.index)}
                            >
                                {members.map((m, idx) => (
                                    <AccordionTab
                                        key={`member-${m.id || idx}`}
                                        header={<MemberHeader member={m} />}
                                    >
                                        <MemberDetails
                                            member={m}
                                            actioning={actioning}
                                            onApprove={handleApprove}
                                            onDeny={openDenyDialog}
                                            onRemove={handleRemove}
                                        />
                                    </AccordionTab>
                                ))}
                            </Accordion>
                            <Paginator
                                first={(page - 1) * limit}
                                rows={limit}
                                totalRecords={totalRecords}
                                onPageChange={(e) => setPage(Math.floor(e.first / limit) + 1)}
                                template="PrevPageLink PageLinks NextPageLink CurrentPageReport"
                                currentPageReportTemplate="{first}-{last} of {totalRecords}"
                                pageLinkSize={3}
                                className="text-sm mt-2"
                            />
                        </>
                    )}
                </div>

                {/* Desktop (≥ md): DataTable with frozen columns */}
                <div className="hidden md:block">
                    <DataTable
                        value={members}
                        dataKey="id"
                        loading={loading}
                        paginator
                        rows={limit}
                        totalRecords={totalRecords}
                        lazy
                        first={(page - 1) * limit}
                        onPage={(e) => setPage(Math.floor(e.first / limit) + 1)}
                        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport"
                        currentPageReportTemplate="Showing {first}-{last} of {totalRecords}"
                        paginatorClassName="text-sm"
                        pageLinkSize={3}
                        responsiveLayout="scroll"
                        scrollable
                        stripedRows
                        showGridlines
                        size="small"
                        className="p-datatable-gridlines"
                        emptyMessage={
                            <div className="flex flex-column align-items-center py-5">
                                <i className="pi pi-users pi-2x text-400 mb-3" aria-hidden="true" />
                                <span className="text-600">No employees found</span>
                            </div>
                        }
                    >
                        <Column
                            header="Employee"
                            body={memberTemplate}
                            frozen
                            alignFrozen="left"
                            style={{ minWidth: '220px' }}
                        />
                        <Column field="phone" header="Phone" body={phoneTemplate} style={{ minWidth: '140px' }} />
                        <Column field="headline" header="Role / Headline" body={headlineTemplate} style={{ minWidth: '180px' }} />
                        <Column header="Status" body={statusTemplate} style={{ minWidth: '120px' }} />
                        <Column header="Action Date" body={actionDateTemplate} style={{ minWidth: '150px' }} />
                        <Column field="lending_network_denied_reason" header="Denial Reason" body={denialReasonTemplate} style={{ minWidth: '180px' }} />
                        <Column
                            header="Actions"
                            body={actionsTemplate}
                            frozen
                            alignFrozen="right"
                            style={{ minWidth: '70px', width: '70px' }}
                        />
                    </DataTable>
                </div>
            </Card>

            {/* Deny dialog (P4 mobile-safe) */}
            <Dialog
                visible={denyDialogVisible}
                onHide={() => setDenyDialogVisible(false)}
                header={`Deny: ${selectedProfile?.full_name || selectedProfile?.email || 'Member'}`}
                modal
                draggable={false}
                style={{ width: '90vw', maxWidth: '450px' }}
                footer={
                    <div className="flex justify-content-end gap-2">
                        <Button
                            label="Cancel"
                            severity="secondary"
                            outlined
                            size="small"
                            onClick={() => setDenyDialogVisible(false)}
                        />
                        <Button
                            label="Deny Member"
                            icon="pi pi-times"
                            severity="danger"
                            size="small"
                            loading={actioning}
                            onClick={handleDeny}
                        />
                    </div>
                }
            >
                <div className="field">
                    <label htmlFor="deny-reason" className="block font-medium text-sm mb-2">
                        Reason <span className="text-500 font-normal">(optional)</span>
                    </label>
                    <InputTextarea
                        id="deny-reason"
                        value={denyReason}
                        onChange={(e) => setDenyReason(e.target.value)}
                        rows={3}
                        maxLength={500}
                        className="w-full"
                        placeholder="Explain why this member is being denied..."
                    />
                    <div className="flex justify-content-between mt-1">
                        <small className="text-500">Visible to the user on the Lending Network page.</small>
                        <small className="text-500">{denyReason.length}/500</small>
                    </div>
                </div>
            </Dialog>

            {/* Reject post dialog (P4 mobile-safe) */}
            <Dialog
                visible={rejectDialogVisible}
                onHide={() => setRejectDialogVisible(false)}
                header={`Reject post by ${selectedPost?.author?.full_name || 'member'}`}
                modal
                draggable={false}
                style={{ width: '90vw', maxWidth: '450px' }}
                footer={
                    <div className="flex justify-content-end gap-2">
                        <Button
                            label="Cancel"
                            severity="secondary"
                            outlined
                            size="small"
                            onClick={() => setRejectDialogVisible(false)}
                        />
                        <Button
                            label="Reject Post"
                            icon="pi pi-times"
                            severity="danger"
                            size="small"
                            loading={postActioning}
                            onClick={handleRejectPost}
                        />
                    </div>
                }
            >
                <div className="field">
                    <label htmlFor="reject-reason" className="block font-medium text-sm mb-2">
                        Reason <span className="text-500 font-normal">(optional)</span>
                    </label>
                    <InputTextarea
                        id="reject-reason"
                        value={rejectReason}
                        onChange={(e) => setRejectReason(e.target.value)}
                        rows={3}
                        maxLength={1000}
                        className="w-full"
                        placeholder="Tell the author why this post wasn't published..."
                    />
                    <div className="flex justify-content-between mt-1">
                        <small className="text-500">Shown to the author, so a rejection isn&apos;t silent.</small>
                        <small className="text-500">{rejectReason.length}/1000</small>
                    </div>
                </div>
            </Dialog>
        </>
    );
};

export default CompanyLendingNetworkAdmin;
